import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import {
  buildInvoiceEmailSubject,
  buildInvoiceEmailMessage,
  parseEmailList,
  isLikelyEmail,
} from '@/lib/notifications';

interface InvoiceEmailTarget {
  folio: string;
  client_name?: string;
  total_amount: number;
}

interface SendInvoiceEmailModalProps {
  open: boolean;
  onClose: () => void;
  invoice: InvoiceEmailTarget | null;
  initialRecipients: string[];
  onSent?: () => void;
}

// Límite blando por archivo para no exceder los límites del servicio de correo.
const MAX_FILE_BYTES = 10 * 1024 * 1024;

export default function SendInvoiceEmailModal({
  open,
  onClose,
  invoice,
  initialRecipients,
  onSent,
}: SendInvoiceEmailModalProps) {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Re-inicializa el formulario cada vez que se abre para una factura distinta.
  useEffect(() => {
    if (!open || !invoice) return;
    setRecipients(initialRecipients.length > 0 ? initialRecipients : []);
    setRecipientInput('');
    setSubject(buildInvoiceEmailSubject());
    setMessage(buildInvoiceEmailMessage(invoice.client_name || 'cliente', Number(invoice.total_amount)));
    setFiles([]);
    setSending(false);
    setError('');
    setSuccess('');
  }, [open, invoice, initialRecipients]);

  function addRecipientsFromInput() {
    const parsed = parseEmailList(recipientInput);
    if (parsed.length === 0) return;
    setRecipients((prev) => {
      const seen = new Set(prev.map((e) => e.toLowerCase()));
      const next = [...prev];
      parsed.forEach((e) => {
        if (!seen.has(e.toLowerCase())) {
          seen.add(e.toLowerCase());
          next.push(e);
        }
      });
      return next;
    });
    setRecipientInput('');
    setError('');
  }

  function removeRecipient(email: string) {
    setRecipients((prev) => prev.filter((e) => e !== email));
  }

  function handleRecipientKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addRecipientsFromInput();
    } else if (e.key === 'Backspace' && recipientInput === '' && recipients.length > 0) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  }

  function handleFilesSelected(selected: FileList | null) {
    if (!selected) return;
    const incoming = Array.from(selected);
    const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`El archivo "${tooBig.name}" supera los 10 MB permitidos.`);
      return;
    }
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      const merged = [...prev];
      incoming.forEach((f) => {
        if (!names.has(f.name)) {
          names.add(f.name);
          merged.push(f);
        }
      });
      return merged;
    });
    setError('');
  }

  function removeFile(name: string) {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  }

  async function handleSend() {
    if (!invoice) return;
    setError('');
    setSuccess('');

    const finalRecipients = (() => {
      const pending = parseEmailList(recipientInput);
      const seen = new Set(recipients.map((e) => e.toLowerCase()));
      const next = [...recipients];
      pending.forEach((e) => {
        if (!seen.has(e.toLowerCase())) {
          seen.add(e.toLowerCase());
          next.push(e);
        }
      });
      return next;
    })();

    if (finalRecipients.length === 0) {
      setError('Agregue al menos un destinatario.');
      return;
    }
    const invalid = finalRecipients.find((e) => !isLikelyEmail(e));
    if (invalid) {
      setError(`El destinatario "${invalid}" no tiene un formato de correo válido.`);
      return;
    }
    if (!subject.trim()) {
      setError('El asunto es obligatorio.');
      return;
    }
    if (!message.trim()) {
      setError('El mensaje es obligatorio.');
      return;
    }

    setSending(true);
    try {
      const amountLabel = new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      }).format(Number(invoice.total_amount));

      // Se arma como multipart/form-data: destinatarios, asunto, mensaje y los
      // adjuntos reales (PDF/XML) viajan como archivos, no como base64.
      const formData = new FormData();
      formData.append('to', JSON.stringify(finalRecipients));
      formData.append('subject', subject.trim());
      formData.append('message', message.trim());
      formData.append('invoice_folio', invoice.folio);
      formData.append('invoice_amount', amountLabel);
      files.forEach((file) => formData.append('attachments', file, file.name));

      const json = await api.mailer.sendInvoiceEmail(formData);
      if (!json || json.ok !== true) {
        throw new Error('El servicio de correo rechazó el envío.');
      }

      setRecipients(finalRecipients);
      setRecipientInput('');
      setSuccess('Correo enviado correctamente.');
      if (onSent) onSent();
      setTimeout(() => {
        onClose();
      }, 900);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Enviar Factura" size="lg">
      {invoice && (
        <div className="space-y-4">
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{success}</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{error}</p>
            </div>
          )}

          <div className="bg-background-50 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Factura</span>
              <p className="text-sm font-semibold text-foreground-900 truncate">{invoice.folio} · {invoice.client_name}</p>
            </div>
            <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">
              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(Number(invoice.total_amount))}
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Para (Destinatarios)</label>
            <div className="flex flex-wrap items-center gap-1.5 w-full px-2.5 py-2 border border-background-200 rounded-lg bg-white focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
              {recipients.map((email) => (
                <span key={email} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                  {email}
                  <button
                    type="button"
                    onClick={() => removeRecipient(email)}
                    className="w-4 h-4 rounded-full hover:bg-black/10 flex items-center justify-center cursor-pointer"
                    title="Quitar destinatario"
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={handleRecipientKeyDown}
                onBlur={addRecipientsFromInput}
                placeholder={recipients.length === 0 ? 'correo@cliente.com' : 'Agregar otro correo...'}
                className="flex-1 min-w-[140px] px-1 py-0.5 text-sm bg-transparent focus:outline-none text-foreground-800 placeholder:text-foreground-400"
              />
            </div>
            <p className="text-[10px] text-foreground-500 mt-1">Escriba y presione Enter o coma para agregar múltiples direcciones.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Asunto</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Mensaje</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 resize-none"
            />
            <p className="text-[10px] text-foreground-400 mt-1 text-right">{message.length}/2000</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Adjuntos</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => handleFilesSelected(e.target.files)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground-600 bg-background-100 border border-background-200 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-attachment-2"></i>
              Seleccionar archivos (PDF, XML...)
            </button>
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((file) => (
                  <div key={file.name} className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-background-50 rounded-lg">
                    <span className="flex items-center gap-2 min-w-0">
                      <i className="ri-file-line text-foreground-500 text-sm"></i>
                      <span className="text-xs text-foreground-700 truncate">{file.name}</span>
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap">{(file.size / 1024).toFixed(0)} KB</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(file.name)}
                      className="w-6 h-6 rounded-md bg-white hover:bg-red-50 flex items-center justify-center cursor-pointer transition-colors"
                      title="Quitar adjunto"
                    >
                      <i className="ri-close-line text-red-500 text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
            >
              <i className={sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
              {sending ? 'Enviando...' : 'Enviar Correo'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}